import React from 'react' 
import PropTypes from 'prop-types'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import Typography from '../common/Typography/index'

const DonutChart = ({ data, title = "", showStats = true }) => {
  const extractData = (responseData) => {
    if (!responseData) return []
    if (Array.isArray(responseData)) return responseData
    if (responseData.data && Array.isArray(responseData.data)) return responseData.data
    return []
  }

  const extractSummary = (responseData) => {
    if (!responseData) return null
    if (responseData.summary) return responseData.summary
    return null
  }

  const getDonutChartData = () => {
    const browserData = extractData(data)
    const browserSummary = extractSummary(data)

    if (!browserSummary || !browserSummary.browserBreakdown) return []

    const browserColors = {
      Chrome: '#ff9800',
      Safari: '#2196f3',
      Edge: '#00bcd4',
      Firefox: '#ff5722',
      Unknown: '#9e9e9e',
      Other: '#795548',
      Opera: '#9c27b0',
      'Mobile Chrome': '#4caf50'
    }

    let browserBreakdown = browserSummary.browserBreakdown

    if (Array.isArray(browserBreakdown)) {
      return browserBreakdown.map((item) => ({
        name: item._id || 'Unknown',
        value: item.count || 0,
        fill: browserColors[item._id] || browserColors.Other
      }))
    } else if (typeof browserBreakdown === 'object') {
      return Object.entries(browserBreakdown).map(([browser, count]) => ({
        name: browser || 'Unknown',
        value: count || 0,
        fill: browserColors[browser] || browserColors.Other
      }))
    }

    return []
  }

  const chartData = getDonutChartData()
  const totalViews = chartData.reduce((sum, item) => sum + item.value, 0)

  if (chartData.length === 0) {
    return (
      <div className="p-4 bg-white rounded-lg border border-gray-200">
        <div className="flex items-center justify-center h-24">
          <Typography className="text-gray-500 text-sm">No data available</Typography>
        </div>
      </div>
    )
  }

  return (
    <div className="p-2 bg-white rounded-lg pr-8">
      <Typography variant="h6" className="mb-4 font-medium text-gray-900">
        {title}
      </Typography>

      <div className="flex items-center gap-4">
        {/* Donut Chart - Smaller */}
        <div className="flex justify-center">
          <ResponsiveContainer width={180} height={180}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={50} // smaller
                outerRadius={80} // smaller
                dataKey="value"
                stroke="none"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.fill} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  fontSize: '10px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Stats - Smaller text & spacing */}
        {showStats && (
          <div className="flex-1 ">
            <div className="space-y-1 pr-2">
              {chartData.slice(0, 4).map((browser, index) => {
                const percentage =
                  totalViews > 0
                    ? ((browser.value / totalViews) * 100).toFixed(1)
                    : 0
                const isPositive = Math.random() > 0.5

                return (
                  <div key={index} className="flex items-start gap-2 ">
                    <div
                      className="w-2 h-2 rounded-full flex-shrink-0 mt-1"
                      style={{ backgroundColor: browser.fill }}
                    ></div>
                    <div className="flex-1">
                      <div className="text-[10px] font-medium text-gray-700">
                        {browser.name}
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="text-[8px] font-bold text-gray-900">
                          {browser.value}
                        </div>
                        <div
                          className={`text-[6px] font-medium ${
                            isPositive ? 'text-green-500' : 'text-red-500'
                          }`}
                        >
                          {percentage}% {isPositive ? '↑' : '↓'}
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

DonutChart.propTypes = {
  data: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  title: PropTypes.string,
  showStats: PropTypes.bool
}

export default DonutChart
